import { router, type Href, type Router } from 'expo-router'
import { type NavigationOptions } from 'expo-router/build/global-state/routing'
import { useMemo } from 'react'
import { loadQuery, useRelayEnvironment } from 'react-relay'
import { type FetchPolicy } from 'relay-runtime'

import {
  RelayQueryMap,
  relayQueryNodes,
  RelayQueryVariablesByName,
  saveRelayPreloadedQuery,
  type RelayQueryName
} from '@/lib'

export type PreloadableRouterOptions<N extends RelayQueryName> = {
  fetchPolicy?: FetchPolicy
  queryName?: N
  queryVariables?: RelayQueryVariablesByName<N>
}

export type PreloadableNavigationOptions<N extends RelayQueryName> = NavigationOptions & PreloadableRouterOptions<N>
export type PreloadableRouterFn<N extends RelayQueryName> = (
  href: Href,
  options?: PreloadableNavigationOptions<N>
) => void
export type PreloadableRouter<N extends RelayQueryName> = Omit<
  Router,
  'dismissTo' | 'navigate' | 'push' | 'replace'
> & {
  dismissTo: PreloadableRouterFn<N>
  navigate: PreloadableRouterFn<N>
  push: PreloadableRouterFn<N>
  replace: PreloadableRouterFn<N>
}

export const usePreloadableRouter = <N extends RelayQueryName>() => {
  const environment = useRelayEnvironment()

  return useMemo<PreloadableRouter<N>>(
    () =>
      // create a proxy that wraps the global router instance, so we can intercept functions without mutating the
      // original instance, since that fucks with navigation state
      new Proxy(router as PreloadableRouter<N>, {
        get(target, prop, receiver) {
          const value = Reflect.get(target, prop, receiver)

          if (typeof value === 'function') {
            // these are the functions we are intercepting, to handle preloading configs, if any
            if (prop === 'dismissTo' || prop === 'navigate' || prop === 'push' || prop === 'replace') {
              return (href: Href, options?: PreloadableNavigationOptions<N>) => {
                // split out our preloadable options
                const { fetchPolicy, queryName, queryVariables, ...originalOptions } = options || {}

                // this is the original router function, bound to the original instance, with preload options stripped out
                const navigate = () => value.call(target, href, originalOptions)

                if (queryName) {
                  const variables = queryVariables ?? {}

                  // load it up
                  const preloadedQuery = loadQuery<RelayQueryMap[N]>(
                    environment,
                    relayQueryNodes[queryName],
                    variables,
                    {
                      fetchPolicy
                    }
                  )

                  // save it
                  saveRelayPreloadedQuery(queryName, variables, preloadedQuery)
                }

                return navigate()
              }
            }

            // bind other functions to the original router instance
            return value.bind(target)
          }

          // anything else
          return value
        }
      }),
    [environment]
  )
}
